import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspectConfig } from '@adornis/filter/aspect-schemas/StringAspect.js';
import type { FilterEditorOptions } from '@adornis/filter/filter-options.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@chancenlogistik/client/components/logistik-component-base-header.js';
import '@chancenlogistik/client/components/logistik-component-base-page-layout.js';
import '@chancenlogistik/client/components/logistik-component-shipping-details.js';
import { LogistikRoutes } from '@chancenlogistik/client/routes.js';
import { buildChancenDataViewOptions } from '@chancenwerk/client/design/components/cr-data-view.js';
import '@chancenwerk/client/design/components/cr-grid.js';
import '@chancenwerk/components/chancen-component-foundation-picker.js';
import { LogistikSchoolAccessToken, LogistikSchoolAccessTokenAPI } from '@chancenwerk/db/tokens.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-search-selection.js';
import '@chancenwerk/design/components/cr-loading-state.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import { PartnerPublicInformationAPI } from '@chancenwerk/public-access/api/PartnerPublicInformationAPI.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { map, of, switchMap } from 'rxjs';

const tokenSelectionSet: BaseQLSelectionSet<LogistikSchoolAccessToken> = selectionSet(
  () => LogistikSchoolAccessToken,
  2,
);

/**
 * @customElement logistik-page-access
 */
@customElement('logistik-page-access')
export class LogistikPageAccess extends ChemistryLitElement {
  private readonly _columnDefinitions: Array<ColumnDefinition<typeof LogistikSchoolAccessToken>> = [
    {
      columnName: 'Token',
      fieldName: 'code',
      isSortable: true,
      columnWidth: 'min-content',
      filterAspect: StringAspectConfig(),
    },
    {
      columnName: 'Förderpartner:in',
      customID: 'foundationName',
      isSortable: true,
      renderItem: entity => (entity.foundation?.name ? html` <cr-text>${entity.foundation.name} </cr-text> ` : nothing),
    },
    {
      columnName: 'Schule',
      fieldName: 'schoolName',
      isSortable: true,
      renderItem: entity => (entity.schoolName ? html` <cr-text> ${entity.schoolName} </cr-text> ` : nothing),
    },
    { columnName: 'Straße', fieldName: 'schoolAddress.strasse', isSortable: true },
    { columnName: 'Ort', fieldName: 'schoolAddress.zipCity', isSortable: true },
    {
      columnName: 'Verfügbar bis',
      fieldName: 'validUntil',
      isSortable: true,
      renderItem: entity => html`
        <cr-text tone=${entity.validUntil < DateTime.now() ? 'error' : ''}>
          ${entity.validUntil.toFormat('dd.LL.yyyy')}
        </cr-text>
      `,
    },
    {
      customID: '_actions',
      isSortable: false,
      renderItem: entity => html`
        <x-flex padding="sm" crossaxis-center horizontal space="xs">
          <cr-button
            mode="icon"
            icon="content_copy"
            @click=${async () => {
              try {
                await navigator.clipboard.writeText(entity.code.toString());
                void ChromiumSnackbar.show('Token erfolgreich kopiert');
              } catch {}
            }}
          ></cr-button>
          <cr-button mode="icon" icon="edit" @click=${() => this._editToken(entity)}></cr-button>
          <cr-button
            mode="icon"
            icon="delete"
            tone="error"
            @click=${async () => {
              if (await ChromiumDialog.confirm('Möchten Sie den Zugang durch diesen Token wirklich widerrufen?')) {
                try {
                  await ChromiumDialog.waitFor(entity.remove());
                } catch (err) {
                  logger.error({ err }, 'Es gab ein Problem beim Entfernen des AccessTokens.');
                  void ChromiumDialog.alert('Es gab ein Problem beim widerrufen dieses Zugangs');
                }
              }
            }}
          ></cr-button>
        </x-flex>
      `,
    },
  ];

  override render() {
    return html`
      <logistik-component-base-page-layout>
        <logistik-component-base-header
          pageTitle=${'Externe Zugänge zur CHANCENlogistik'}
          backRoute=${LogistikRoutes.landing}
        ></logistik-component-base-header>
        <x-flex space="lg">
          <x-flex space="md">
            <cr-text>
              Den folgenden Link können Sie mit Kooperationspartnern teilen, damit diese über die CHANCENlogistik
              Lernhefte bestellen können. Zusätzlich zu dem Link wird ein Zugangscode benötigt, den Sie unten generieren
              können.
            </cr-text>
            ${this._link()}
          </x-flex>
          <x-flex space="md">
            <cr-text> Generieren und verwalten Sie Zugangscodes für die Kooperationspartner </cr-text>
            <x-flex horizontal end>
              <cr-button mode="outline" icon="add" @click=${() => this._addNewToken()}>
                Neuen Zugangscode erstellen
              </cr-button>
            </x-flex>
            ${this._dataView()}
          </x-flex>
        </x-flex>
      </logistik-component-base-page-layout>
    `;
  }

  private _link() {
    return html`
      <cr-card
        href="#"
        horizontal
        crossaxis-center
        space="md"
        space-between
        @click=${async () => {
          try {
            await navigator.clipboard.writeText(A.absoluteUrl('/external'));
            void ChromiumSnackbar.show('Link erfolgreich kopiert');
          } catch {}
        }}
      >
        <cr-text> ${A.absoluteUrl('/external')} </cr-text> <cr-icon> content_copy </cr-icon>
      </cr-card>
    `;
  }

  private _dataView(): Renderable {
    const dataViewOptions = {
      class: LogistikSchoolAccessToken,
      operation: LogistikSchoolAccessTokenAPI.subscribePaginatedFiltered,
      emptyState: () => this._getEmptyState(),
      selectionSet: tokenSelectionSet,
      isSortable: true,
      onlyShowLastTranslation: true,
      customColumns: this._columnDefinitions,
      view: new View({ filter: new AdornisFilter({}) }),
    } as DataViewOptions<typeof LogistikSchoolAccessToken>;
    const filterOptions = {
      showAspectsPicker: true,
      fieldAspectGeneration: {
        hideFieldsWithoutTranslation: true,
        whitelist: {
          code: 1,
          schoolName: 1,
          foundationName: 1,
          schoolAddress: {
            strasse: 1,
            country: 1,
            zipCity: {
              city: 1,
              zip: 1,
            },
          },
          validUntil: 1,
        },
      },
    } as Omit<FilterEditorOptions<typeof LogistikSchoolAccessToken>, 'filter' | 'class'>;
    const options = buildChancenDataViewOptions(dataViewOptions, filterOptions);
    return html`
      <cr-card padding="sm" margin="sm" ${css({ flexShrink: '0', position: 'sticky', left: 0 })}>
        <x-filter-editor .options=${options.filterOptions}> </x-filter-editor>
      </cr-card>

      <cr-data-view .options=${options.dataViewOptions}> </cr-data-view>
    `;
  }

  private _getEmptyState() {
    return html` <cr-text>Es wurden noch keine Tokens hinzugefügt. Hier gibt es nichts zu sehen :(</cr-text> `;
  }

  private _editToken(token: LogistikSchoolAccessToken) {
    return LogistikAccessCreateNewToken.showPopup({ props: { token } });
  }

  private _addNewToken() {
    return LogistikAccessCreateNewToken.showPopup();
  }
}

@customElement('logistik-access-create-new-token')
export class LogistikAccessCreateNewToken extends ChromiumDialog<void> {
  static override get element_name() {
    return 'logistik-access-create-new-token';
  }

  @property({ attribute: false }) private readonly token = new RXController<Maybe<LogistikSchoolAccessToken>>(
    this,
    null,
  );
  @state() private readonly _form = new FormController(
    this,
    this.token.observable.pipe(map(token => token ?? new LogistikSchoolAccessToken({}))),
    {
      success: async token => {
        try {
          await ChromiumDialog.waitFor(token._id ? token.save() : token.create());
          void ChromiumSnackbar.show(
            this.token.value ? 'Token wurde erfolgreich gespeichert' : 'Neues Token wurde erfolgreich erstellt',
          );
          void this.close();
        } catch (err) {
          logger.error({ err }, 'Es gab ein Problem beim erstellen eines neuen AccessTokens');
          void ChromiumDialog.alert('Es gab ein Problem beim erstellen eines neuen Token!');
        }
      },
    },
  );
  @state() private readonly _selectedSchool = new RXController(this, this.token.observable, token$ =>
    token$.pipe(
      switchMap(token =>
        token?.schoolID
          ? PartnerPublicInformationAPI.getByID([token.schoolID])({
              _id: 1,
              _class: 1,
              name: 1,
              address: {
                _class: 1,
                strasse: 1,
                country: 1,
                zipCity: {
                  _class: 1,
                  zip: 1,
                  city: 1,
                },
              },
              bundesland: 1,
            })
          : of([]),
      ),
      map(schools => schools[0]),
    ),
  );

  override content() {
    return html`
      <x-flex space="lg" padding="lg">
        <cr-h3> Schul-Daten eingeben </cr-h3>
        <x-flex space="md">
          <cr-h4> Schule auswählen </cr-h4>
          <chancen-component-public-school-picker
            placeholder="Standort*"
            emptyState="Keine Standorte gefunden"
            @value-picked=${async (e: ValueEvent<Maybe<Partner>>) => {
              if (!this._form.document || !e.detail.value) return;
              this._form.document.schoolID = e.detail.value?._id;
              this._form.document.schoolAddress = e.detail.value?.address;
              this._form.document.schoolName = e.detail.value.name;
              this.requestUpdate();
            }}
            .value=${this._selectedSchool.value}
          ></chancen-component-public-school-picker>
        </x-flex>
        <x-flex space="md">
          <cr-h4> Optional alternative Lieferadresse angeben </cr-h4>
          <cr-input placeholder="Schulname*" ${this._form.field('schoolName')}></cr-input>
          <cr-input placeholder="z.H." ${this._form.field('zuHaenden')}></cr-input>
          <chancen-component-address-picker
            hide-all-countries
            ${this._form.field('schoolAddress')}
          ></chancen-component-address-picker>
          <cr-date-picker-dropdown
            placeholder="Verfügbar bis"
            ${this._form.field('validUntil')}
          ></cr-date-picker-dropdown>
          <cr-input
            placeholder="Maximale Bestellmenge (pro Bestellung)"
            step="1"
            type="number"
            ${this._form.field('maxOrderQuantity')}
          ></cr-input>
        </x-flex>
        <x-flex horizontal end space="md">
          <cr-button mode="outline" @click=${() => this.cancel()}> Abbrechen </cr-button>
          <cr-button @click=${() => this._form.validate()}>
            ${this.token.value ? 'Speichern' : 'Hinzufügen'}
          </cr-button>
        </x-flex>
      </x-flex>
    `;
  }
}
